import type { StatusTone } from '../../ui/StatusBadge/StatusBadge'
import { adoptionPrerequisite } from '../onboarding/ExistingClinicAdoption'
import { communicationsSetupCopy } from '../onboarding/communications-setup-presentation'
import { OnboardingApiError } from '../onboarding/onboarding-api'
import type {
  AdministratorInvitation,
  ConfirmedOrganizationClinic,
  ManagerAccess,
  ManagerInvitation,
  OrganizationAdministrator,
  OrganizationCommunications,
  TwilioConnection,
} from '../onboarding/organization-onboarding-contracts'
import type { ReadSideStatus } from '../read-side/read-side-status'
import { formatPhone } from '../patients/patient-display'

/** Presentation of Settings > Organization. The server owns every decision. */

export function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`
}

export function managerLabel(manager: ManagerAccess) {
  return manager.email ?? `Manager ${manager.targetUid.slice(0, 8)}`
}

export function administratorLabel(administrator: OrganizationAdministrator) {
  return (
    administrator.email ??
    `Administrator ${administrator.targetUid.slice(0, 8)}`
  )
}

/** One or two letters for a person's avatar, from their email. */
export function initials(label: string) {
  const name = label.split('@')[0] ?? label
  const parts = name.split(/[._-]+/).filter(Boolean)
  const letters =
    parts.length > 1
      ? `${parts[0]![0]}${parts[1]![0]}`
      : name.slice(0, 1) || '?'
  return letters.toUpperCase()
}

/** Invitations someone can still act on: pending, or being accepted. */
export function openInvitations<
  T extends ManagerInvitation | AdministratorInvitation,
>(invitations: T[]) {
  return invitations.filter(
    (invitation) =>
      invitation.status === 'CLAIMING' ||
      (invitation.status === 'ACTIVE' && !invitation.expired)
  )
}

export function invitationStatus(
  invitation: ManagerInvitation | AdministratorInvitation
) {
  if (invitation.expired) return 'Expired'
  if (invitation.status === 'ACTIVE') {
    return invitation.deliveryStatus === 'ACCEPTED'
      ? 'Pending acceptance'
      : 'Delivery pending'
  }
  if (invitation.status === 'CLAIMING') return 'Acceptance in progress'
  if (invitation.status === 'CLAIMED') return 'Accepted'
  return 'Revoked'
}

/** The clinic's onboarding step that is still open, if any. */
export function onboardingStep(clinic: ConfirmedOrganizationClinic) {
  return clinic.onboardingStatus && clinic.onboardingStatus !== 'READY'
    ? adoptionPrerequisite(clinic.onboardingStatus)
    : null
}

export type StatusView = {
  label: string
  tone: StatusTone
  /** Still waiting for the first answer: show a placeholder, not a guess. */
  pending: boolean
}

/** The clinic workspace (PMS read side), independent of Communications. */
export function workspaceStatus(
  status: ReadSideStatus | undefined,
  { dashboardReady, isError }: { dashboardReady: boolean; isError: boolean }
): StatusView {
  const state = status?.state
  if (state === 'READY')
    return { label: 'Ready', tone: 'success', pending: false }
  if (state === 'SYNCING')
    return { label: 'Syncing', tone: 'info', pending: false }
  if (state === 'INITIALIZING')
    return { label: 'Initializing', tone: 'neutral', pending: false }
  if (state === 'DEGRADED_READ')
    return { label: 'Degraded', tone: 'warning', pending: false }
  if (status && state === null)
    return dashboardReady
      ? { label: 'Ready', tone: 'success', pending: false }
      : { label: 'Setup required', tone: 'neutral', pending: false }
  if (isError) return { label: 'Unavailable', tone: 'neutral', pending: false }
  return { label: 'Checking', tone: 'neutral', pending: true }
}

export type PhoneView = StatusView & {
  /** Verified by a live, read-only Twilio check. */
  connected: boolean
  needsAttention: boolean
  /** The canonical assigned number; null when none; undefined when unknown. */
  number: string | null | undefined
  numberPending: boolean
  detail: string | null
  actionLabel: string
}

/**
 * Phone & texting for one clinic. The canonical Communications read owns the
 * assigned number and setup state; only the provider check says Connected.
 */
export function phoneStatus({
  state,
  verified,
  fallbackStatus,
  verificationPending,
  verificationFailed,
  readFailed,
  readPending,
}: {
  state: OrganizationCommunications | undefined
  verified: TwilioConnection | undefined
  fallbackStatus: ConfirmedOrganizationClinic['communicationsStatus']
  verificationPending: boolean
  verificationFailed: boolean
  readFailed: boolean
  readPending: boolean
}): PhoneView {
  const base = verified
    ? (
        {
          CONNECTED: { label: 'Connected', tone: 'success' },
          NEEDS_REPAIR: { label: 'Needs repair', tone: 'warning' },
          SETUP_REQUIRED: { label: 'Not set up', tone: 'neutral' },
          PROVISIONING: { label: 'Setting up', tone: 'info' },
          BLOCKED: { label: 'Blocked', tone: 'warning' },
        } as const
      )[verified.status]
    : verificationFailed
      ? ({ label: 'Couldn’t verify', tone: 'neutral' } as const)
      : ({ label: 'Checking', tone: 'neutral' } as const)
  const connected = verified?.status === 'CONNECTED'
  const number = state
    ? state.publicNumber
    : verified
      ? verified.publicNumber
      : undefined
  const continuing =
    ['PROVISIONING', 'VALIDATING', 'PARTIAL', 'BLOCKED'].includes(
      state?.status ?? ''
    ) || ['IN_PROGRESS', 'NEEDS_ATTENTION'].includes(fallbackStatus)
  const detail =
    verified?.status === 'NEEDS_REPAIR'
      ? 'Twilio routing differs from Flio’s verified configuration. Review the connection before making changes.'
      : state && !connected
        ? communicationsSetupCopy(state)
        : readFailed || verificationFailed
          ? 'Phone & texting details are unavailable right now. The clinic workspace is not affected.'
          : null
  return {
    ...base,
    pending: !verified && !verificationFailed && verificationPending,
    connected,
    needsAttention:
      verified?.status === 'NEEDS_REPAIR' || verified?.status === 'BLOCKED',
    number,
    numberPending: number === undefined && readPending && !verified,
    detail,
    actionLabel: connected
      ? 'Manage phone & texting'
      : verified?.status === 'NEEDS_REPAIR'
        ? 'Repair connection'
        : continuing
          ? 'Continue setup'
          : number
            ? 'Manage phone & texting'
            : 'Set up phone & texting',
  }
}

export function phoneNumberLabel(number: string | null | undefined) {
  if (number === undefined) return '—'
  return number ? (formatPhone(number) ?? number) : 'No number'
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** What is wrong with an email before it is sent, if anything. */
export function emailProblem(value: string) {
  const email = value.trim()
  if (!email) return 'Enter an email address.'
  if (!EMAIL.test(email)) return 'Enter a valid email address.'
  return null
}

export function invitationError(error: unknown) {
  if (error instanceof OnboardingApiError) {
    if (error.code === 'invitation-account-unusable')
      return 'This email belongs to a disabled Flio account. Contact Flio support before inviting it.'
    if (error.code === 'invitation-delivery-failed')
      return 'The invitation email couldn’t be sent. No access was granted. Try again.'
    if (error.code === 'invitation-conflict')
      return 'An invitation for this email is being accepted. Try again later.'
    if (error.code === 'forbidden')
      return 'You no longer have permission to invite people to this organization.'
  }
  return 'The invitation couldn’t be sent safely. Refresh and try again.'
}

export function cancelInvitationError(error: unknown) {
  if (error instanceof OnboardingApiError && error.code === 'forbidden')
    return 'You no longer have permission to cancel this invitation.'
  return 'The invitation changed or couldn’t be cancelled safely. Refresh and try again.'
}

export function managerAccessError(error: unknown) {
  if (error instanceof OnboardingApiError) {
    if (error.code === 'manager-reconciliation-required')
      return 'This manager’s access is being reconciled. Try again in a minute.'
    if (error.code === 'forbidden')
      return 'You no longer have permission to change manager access.'
  }
  return 'Manager access changed or couldn’t be updated safely. Refresh and try again.'
}
